// lib/BaseSolver.ts
var BaseSolver = class {
  MAX_ITERATIONS = 1e5;
  solved = false;
  failed = false;
  iterations = 0;
  progress = 0;
  error = null;
  activeSubSolver;
  failedSubSolvers;
  timeToSolve;
  stats = {};
  _setupDone = false;
  getSolverName() {
    return this.constructor.name;
  }
  setup() {
    if (this._setupDone) return;
    this._setup();
    this._setupDone = true;
  }
  /** Override this method to perform setup logic */
  _setup() {
  }
  /** DO NOT OVERRIDE! Override _step() instead */
  step() {
    if (!this._setupDone) {
      this.setup();
    }
    if (this.solved) return;
    if (this.failed) return;
    this.iterations++;
    try {
      this._step();
    } catch (e) {
      this.error = `${this.getSolverName()} error: ${e}`;
      this.failed = true;
      throw e;
    }
    if (!this.solved && this.iterations >= this.MAX_ITERATIONS) {
      this.tryFinalAcceptance();
    }
    if (!this.solved && this.iterations >= this.MAX_ITERATIONS) {
      this.error = `${this.getSolverName()} ran out of iterations`;
      this.failed = true;
    }
    if ("computeProgress" in this) {
      this.progress = this.computeProgress();
    }
  }
  /** Override this method to implement solver logic */
  _step() {
  }
  getConstructorParams() {
    throw new Error("getConstructorParams not implemented");
  }
  /**
   * Override this method to return the standardized output of the solver.
   * This method should only be called after the solver has completed successfully.
   * Returns null by default - solvers with outputs should override this method.
   */
  getOutput() {
    return null;
  }
  solve() {
    const startTime = Date.now();
    while (!this.solved && !this.failed) {
      this.step();
    }
    const endTime = Date.now();
    this.timeToSolve = endTime - startTime;
  }
  visualize() {
    return {
      lines: [],
      points: [],
      rects: [],
      circles: []
    };
  }
  /**
   * Called when the solver is about to fail, but we want to see if we have an
   * "acceptable" or "passable" solution. Mostly used for optimizers that
   * have an aggressive early stopping criterion.
   */
  tryFinalAcceptance() {
  }
  /**
   * A lightweight version of the visualize method that can be used to stream
   * progress
   */
  preview() {
    return {
      lines: [],
      points: [],
      rects: [],
      circles: []
    };
  }
};

// lib/BasePipelineSolver.ts
import { setStepOfAllObjects } from "graphics-debug";
function definePipelineStep(solverName, solverClass, getConstructorParams, opts = {}) {
  return {
    solverName,
    solverClass,
    getConstructorParams,
    onSolved: opts.onSolved
  };
}
var BasePipelineSolver = class extends BaseSolver {
  startTimeOfStage = {};
  endTimeOfStage = {};
  timeSpentOnStage = {};
  firstIterationOfStage = {};
  currentPipelineStageIndex = 0;
  inputProblem;
  /** Stores the outputs from each completed pipeline stage */
  pipelineOutputs = {};
  constructor(inputProblem) {
    super();
    this.inputProblem = inputProblem;
    this.MAX_ITERATIONS = 1e6;
  }
  _step() {
    const pipelineStageDef = this.pipelineDef[this.currentPipelineStageIndex];
    if (!pipelineStageDef) {
      this.solved = true;
      return;
    }
    if (this.activeSubSolver) {
      this.activeSubSolver.step();
      if (this.activeSubSolver.solved) {
        this.endTimeOfStage[pipelineStageDef.solverName] = performance.now();
        this.timeSpentOnStage[pipelineStageDef.solverName] = this.endTimeOfStage[pipelineStageDef.solverName] - this.startTimeOfStage[pipelineStageDef.solverName];
        const output = this.activeSubSolver.getOutput();
        if (output !== null) {
          this.pipelineOutputs[pipelineStageDef.solverName] = output;
        }
        pipelineStageDef.onSolved?.(this);
        this.activeSubSolver = null;
        this.currentPipelineStageIndex++;
      } else if (this.activeSubSolver.failed) {
        this.error = this.activeSubSolver?.error;
        this.failed = true;
        this.activeSubSolver = null;
      }
      return;
    }
    const constructorParams = pipelineStageDef.getConstructorParams(this);
    this.activeSubSolver = new pipelineStageDef.solverClass(
      ...constructorParams
    );
    this[pipelineStageDef.solverName] = this.activeSubSolver;
    this.timeSpentOnStage[pipelineStageDef.solverName] = 0;
    this.startTimeOfStage[pipelineStageDef.solverName] = performance.now();
    this.firstIterationOfStage[pipelineStageDef.solverName] = this.iterations;
  }
  solveUntilStage(stageName) {
    while (this.getCurrentStageName().toLowerCase() !== stageName.toLowerCase()) {
      this.step();
      if (this.failed || this.solved) break;
    }
  }
  getCurrentStageName() {
    return this.pipelineDef[this.currentPipelineStageIndex]?.solverName ?? "none";
  }
  getStageProgress() {
    const totalStages = this.pipelineDef.length;
    if (totalStages === 0) return 1;
    const currentStageProgress = this.activeSubSolver?.progress ?? 0;
    return (this.currentPipelineStageIndex + currentStageProgress) / totalStages;
  }
  getStageStats() {
    const stats = {};
    for (const stage of this.pipelineDef) {
      const timeSpent = this.timeSpentOnStage[stage.solverName] || 0;
      const firstIteration = this.firstIterationOfStage[stage.solverName] || 0;
      const currentIteration = this.iterations;
      const iterations = stage.solverName === this.getCurrentStageName() ? currentIteration - firstIteration : 0;
      const completed = this.currentPipelineStageIndex > this.pipelineDef.findIndex((s) => s.solverName === stage.solverName);
      stats[stage.solverName] = {
        timeSpent,
        iterations,
        completed
      };
    }
    return stats;
  }
  initialVisualize() {
    return null;
  }
  finalVisualize() {
    return null;
  }
  visualize() {
    if (!this.solved && this.activeSubSolver) {
      return this.activeSubSolver.visualize();
    }
    let stageStepOffset = 0;
    const initialVisualization = this.initialVisualize();
    if (initialVisualization) {
      setStepOfAllObjects(initialVisualization, 0);
      stageStepOffset = 1;
    }
    let finalVisualization = null;
    if (this.solved) {
      finalVisualization = this.finalVisualize();
    }
    const visualizations = [initialVisualization].filter(Boolean).concat(
      this.pipelineDef.map((stage, stageIndex) => {
        const solver = this[stage.solverName];
        const viz = solver?.visualize();
        if (!viz) return null;
        setStepOfAllObjects(viz, stageIndex + stageStepOffset);
        return viz;
      }).filter(Boolean)
    );
    if (visualizations.length === 0) {
      return { points: [], rects: [], lines: [], circles: [], texts: [] };
    }
    if (this.solved && finalVisualization) {
      setStepOfAllObjects(
        finalVisualization,
        visualizations.length + stageStepOffset + 1
      );
      visualizations.push(finalVisualization);
    }
    if (visualizations.length === 1) {
      return visualizations[0];
    }
    return {
      points: visualizations.flatMap((v) => v.points || []),
      rects: visualizations.flatMap((v) => v.rects || []),
      lines: visualizations.flatMap((v) => v.lines || []),
      circles: visualizations.flatMap((v) => v.circles || []),
      texts: visualizations.flatMap((v) => v.texts || [])
    };
  }
  preview() {
    if (this.activeSubSolver) {
      return this.activeSubSolver.preview();
    }
    return super.preview();
  }
  computeProgress() {
    return this.getStageProgress();
  }
  /**
   * Get the output from a specific pipeline stage
   */
  getStageOutput(stageOutput) {
    return this.pipelineOutputs[stageOutput];
  }
  /**
   * Get all pipeline outputs
   */
  getAllOutputs() {
    return { ...this.pipelineOutputs };
  }
  /**
   * Check if a step has completed and produced output
   */
  hasStageOutput(stageName) {
    return stageName in this.pipelineOutputs;
  }
  /**
   * Get a solver instance by name
   */
  getSolver(stageName) {
    return this[stageName];
  }
};
export {
  BasePipelineSolver,
  BaseSolver,
  definePipelineStep
};
